import React, { useRef, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components';
import { Camera24Regular, CameraSwitch24Regular, Dismiss20Regular } from '@fluentui/react-icons';
import './styles.scss';

interface WebcamCaptureProps {
  open: boolean;
  onCapture: (file: File) => void;
  onClose: () => void;
}

const WebcamCapture: React.FC<WebcamCaptureProps> = ({ open, onCapture, onClose }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const [currentFacingMode, setCurrentFacingMode] = useState<'user' | 'environment'>(isMobileDevice ? 'environment' : 'user');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try { track.stop(); } catch {}
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch {}
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = (facing: 'user' | 'environment') => {
    setCameraError(null);
    stopCamera();

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera is not supported in this browser');
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: facing } })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute('playsinline', 'true');
          videoRef.current.play();
        }
      })
      .catch((err) => {
        setCameraError('Unable to access camera: ' + err.message);
      });
  };

  useEffect(() => {
    if (!open) {
      stopCamera();
      return;
    }

    const initialFacing: 'user' | 'environment' = isMobileDevice ? 'environment' : 'user';
    setCurrentFacingMode(initialFacing);
    startCamera(initialFacing);

    if (navigator.mediaDevices?.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        const videoInputs = devices.filter((d) => d.kind === 'videoinput');
        setHasMultipleCameras(videoInputs.length > 1);
      }).catch(() => {});
    }

    return () => {
      stopCamera();
    };
  }, [open]);

  const handleSwitchCamera = () => {
    const newMode = currentFacingMode === 'user' ? 'environment' : 'user';
    setCurrentFacingMode(newMode);
    startCamera(newMode);
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx || !video.videoWidth || !video.videoHeight) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopCamera();
      onCapture(file);
    }, 'image/jpeg', 0.92);
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) handleClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle action={
            <DialogTrigger action="close">
              <Button appearance="subtle" icon={<Dismiss20Regular />} />
            </DialogTrigger>
          }>
            Take a Photo
          </DialogTitle>
          <DialogContent>
            <div className="webcam-capture">
              <video ref={videoRef} muted autoPlay playsInline />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              {cameraError && <div className="webcam-capture__error">{cameraError}</div>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>Cancel</Button>
            {isMobileDevice && (
              <Button appearance="secondary" icon={<CameraSwitch24Regular />} onClick={handleSwitchCamera} disabled={!!cameraError}>
                Switch Camera
              </Button>
            )}
            <Button appearance="primary" icon={<Camera24Regular />} onClick={handleCapture} disabled={!!cameraError}>
              Capture
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default WebcamCapture;
